/**
 * bookingAudit — client + types for the booking action-provenance trail
 * (`GET /api/v1/audit/bookings/:id`, BON-143). `:id` is the ops `Booking.id`
 * UUID. The endpoint returns `{ status: 'ok', entries: BookingAuditEntry[] }`,
 * newest first.
 */
import { apiFetch } from '../lib/apiFetch';

/** The action verbs recorded on the booking trail (see backend BOOKING_ACTION).
 *  `confirmation_resent` / `confirmation_resend_failed` added BON-630 — no
 *  email addresses ever appear in either's `summary` or `context`. */
export type BookingAuditEventType =
  | 'booking_created'
  | 'rebooked'
  | 'refund_issued'
  | 'booking_cancelled'
  | 'confirmation_resent'
  | 'confirmation_resend_failed';

export interface BookingAuditEntry {
  id: string;
  eventType: string;
  /** booking_created only: 'self_service' | 'comp' | 'manual_paid'. */
  origin: string | null;
  /** rebooked only: true when a cutoff override was used. */
  overrideUsed: boolean;
  /** Human one-liner for display. */
  summary: string;
  /** Structured, event-type-specific payload. */
  context: unknown;
  /** Acting user's full name, or "System" for self-service / system actions. */
  actorName: string;
  actorUserId: string | null;
  createdAt: string;
}

/** Shape of `context` on a `rebooked` event (see backend recordRebooked). The
 *  from/to booking ids let the timeline link to the rebooking counterpart. */
export interface RebookedContext {
  fromBookingId?: string;
  toBookingId?: string;
  fromWorkshopId?: string;
  toWorkshopId?: string;
  reason?: string;
  overrideUsed?: boolean;
  overrideCapability?: string;
  cutoffAt?: string;
  /** Which end of the move this entry sits on (BON-146). Two entries are
   *  written per rebook, one per booking, and each carries its own `summary`
   *  ("Rebooked to another workshop" / "…from…") — the timeline renders that
   *  verbatim, so this is here for anything that needs to branch structurally
   *  rather than read the sentence. */
  direction?: 'moved_out' | 'moved_in';
  /** The cash that moved with the booking, in cents. Null on a comp. */
  amountCents?: number | null;
  paymentIntentId?: string | null;
}

/** Shape of `context` on a `refund_issued` event (see backend
 *  `recordRefundIssued`, BON-147 / widened BON-335). `giftCardId` is set only
 *  for a gift-card refund; `externalReference` only for a recorded (card or
 *  other-rail) refund with one given — the two never coexist. */
export interface RefundIssuedContext {
  amountCents?: number;
  currency?: string;
  refundType?: string;
  method?: string;
  reason?: string;
  giftCardId?: string;
  /** The passes a gift-pass refund issued, one per seat. */
  giftPassIds?: string[];
  externalReference?: string;
  stripeRefundId?: string;
}

interface BookingAuditResponse {
  status: string;
  entries: BookingAuditEntry[];
}

export async function fetchBookingAudit(id: string): Promise<BookingAuditEntry[]> {
  const res = await apiFetch(`/api/v1/audit/bookings/${id}`);
  if (!res.ok) {
    throw new Error(`Failed to load booking audit: ${res.status}`);
  }
  const body = (await res.json()) as BookingAuditResponse;
  return body.entries;
}
